// Renders the End-of-Day wrap-up to a 1080×1350 PNG (Instagram story aspect)
// suitable for sharing as a file via the Web Share API. Pulls the same
// numbers the share-as-text path used and lays them out as a navy-gradient
// summary card with the day's date, total revenue/cuts/hours, and an
// optional list of cuts.

type AggCut = { timeLabel: string; cutTypeName: string; totalPrice: number };

export type EodImageInput = {
  dateLabel: string; // already-localized, e.g. "Friday, May 5"
  totals: {
    cuts: number;
    chairTimeMin: number;
    totalRevenue: number;
  };
  cuts: AggCut[];
  labels: {
    titleEyebrow: string; // e.g. "TODAY'S WRAP-UP"
    headline: string;     // e.g. "Friday, May 5"
    cutsLabel: string;    // e.g. "cuts"
    revenueLabel: string; // e.g. "in revenue"
    chairLabel: string;   // e.g. "h on the chair" (with var) — caller pre-formats
    cutsHeader: string;   // e.g. "Today's cuts"
  };
};

const W = 1080;
const H = 1350;

export async function renderEodImage(input: EodImageInput): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2d context unavailable');

  // Background — navy gradient with a soft gold radial accent
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, '#0c1530');
  bg.addColorStop(1, '#1a2954');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  const glow = ctx.createRadialGradient(W * 0.85, H * 0.18, 80, W * 0.85, H * 0.18, 600);
  glow.addColorStop(0, 'rgba(212,169,66,0.35)');
  glow.addColorStop(1, 'rgba(212,169,66,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // Eyebrow
  ctx.fillStyle = '#d4a942';
  ctx.font = 'bold 28px system-ui, -apple-system, sans-serif';
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  drawSpacedText(ctx, input.labels.titleEyebrow.toUpperCase(), 90, 110, 6);

  // Headline date
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 84px Georgia, "Times New Roman", serif';
  ctx.fillText(input.labels.headline, 90, 160);

  // KPI block — three big numbers stacked
  const kpiTop = 360;
  drawKpi(ctx, 90, kpiTop, '$' + input.totals.totalRevenue.toLocaleString(), input.labels.revenueLabel);
  drawKpi(ctx, 90, kpiTop + 200, String(input.totals.cuts), input.labels.cutsLabel);
  drawKpi(ctx, 90, kpiTop + 400, formatChair(input.totals.chairTimeMin), input.labels.chairLabel);

  // Divider
  ctx.strokeStyle = 'rgba(255,255,255,0.18)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(90, kpiTop + 600);
  ctx.lineTo(W - 90, kpiTop + 600);
  ctx.stroke();

  // Cuts list (max 6 — keep it readable)
  if (input.cuts.length > 0) {
    ctx.fillStyle = '#d4a942';
    ctx.font = 'bold 24px system-ui, -apple-system, sans-serif';
    drawSpacedText(ctx, input.labels.cutsHeader.toUpperCase(), 90, kpiTop + 630, 4);

    ctx.font = '32px system-ui, -apple-system, sans-serif';
    const top = kpiTop + 680;
    const rowH = 50;
    const visible = input.cuts.slice(0, 6);
    visible.forEach((c, i) => {
      const y = top + i * rowH;
      ctx.fillStyle = 'rgba(255,255,255,0.55)';
      ctx.fillText(c.timeLabel, 90, y);
      ctx.fillStyle = '#ffffff';
      const namePad = 230;
      let name = c.cutTypeName;
      const maxNameWidth = W - namePad - 220;
      while (ctx.measureText(name).width > maxNameWidth && name.length > 0) {
        name = name.slice(0, -1);
      }
      if (name !== c.cutTypeName) name = name.slice(0, -1) + '…';
      ctx.fillText(name, namePad, y);
      ctx.textAlign = 'right';
      ctx.fillStyle = '#d4a942';
      ctx.fillText('$' + c.totalPrice, W - 90, y);
      ctx.textAlign = 'left';
    });
    if (input.cuts.length > visible.length) {
      const y = top + visible.length * rowH;
      ctx.fillStyle = 'rgba(255,255,255,0.55)';
      ctx.font = 'italic 28px system-ui, -apple-system, sans-serif';
      ctx.fillText(`+ ${input.cuts.length - visible.length} more`, 90, y);
    }
  }

  // Footer wordmark
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.font = 'bold 26px system-ui, -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  drawSpacedText(ctx, 'BUSY BARBER', W / 2, H - 60, 5);

  return await canvasToBlob(canvas, 'image/png');
}

function drawKpi(ctx: CanvasRenderingContext2D, x: number, y: number, value: string, label: string) {
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 96px Georgia, "Times New Roman", serif';
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  ctx.fillText(value, x, y);
  ctx.fillStyle = 'rgba(255,255,255,0.7)';
  ctx.font = '32px system-ui, -apple-system, sans-serif';
  ctx.fillText(label, x, y + 110);
}

function formatChair(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('canvas.toBlob failed'))), type, 0.92);
  });
}

function drawSpacedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  spacing: number,
) {
  let cur = x;
  for (const ch of text) {
    ctx.fillText(ch, cur, y);
    cur += ctx.measureText(ch).width + spacing;
  }
}
